import { useEffect, useRef } from "react";

export function MatrixCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const fontSize = 13;
    const targetFrameDurationMs = 1000 / 30;
    const maxDevicePixelRatio = 1.25;
    const chars =
      "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ<>{}[]|\\;:,.?/";

    let drops: number[] = [];
    let viewportWidth = window.innerWidth;
    let viewportHeight = window.innerHeight;
    let animId = 0;
    let resizeFrameId = 0;
    let lastFrameTime = 0;
    let paused = false;

    const applyResize = () => {
      viewportWidth = window.innerWidth;
      viewportHeight = window.innerHeight;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, maxDevicePixelRatio);
      canvas.width = Math.floor(viewportWidth * pixelRatio);
      canvas.height = Math.floor(viewportHeight * pixelRatio);
      canvas.style.width = `${viewportWidth}px`;
      canvas.style.height = `${viewportHeight}px`;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      const columns = Math.floor(viewportWidth / fontSize);
      drops = Array.from({ length: columns }, () => Math.random() * -100);
      ctx.font = `${fontSize}px monospace`;
    };

    const resize = () => {
      if (resizeFrameId) {
        cancelAnimationFrame(resizeFrameId);
      }
      resizeFrameId = requestAnimationFrame(() => {
        resizeFrameId = 0;
        applyResize();
      });
    };

    const draw = (nowMs: number) => {
      animId = requestAnimationFrame(draw);
      if (document.hidden || nowMs - lastFrameTime < targetFrameDurationMs) return;
      lastFrameTime = nowMs;

      ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
      ctx.fillRect(0, 0, viewportWidth, viewportHeight);

      for (let index = 0; index < drops.length; index += 1) {
        const character = chars[Math.floor(Math.random() * chars.length)];
        const alpha = Math.random() * 0.5 + 0.1;
        ctx.fillStyle = `rgba(0, 255, 65, ${alpha})`;
        const dropY = drops[index] * fontSize;
        if (dropY > 0) ctx.fillText(character, index * fontSize, dropY);
        if (dropY > viewportHeight && Math.random() > 0.975) drops[index] = 0;
        drops[index] += 0.5;
      }
    };

    const startLoop = () => {
      animId = requestAnimationFrame(draw);
    };

    const isTouchMobile =
      window.matchMedia("(pointer: coarse)").matches ||
      navigator.maxTouchPoints > 0 ||
      "ontouchstart" in window;
    const contactSection = document.querySelector("contact");
    let observer: IntersectionObserver | null = null;

    if (!isTouchMobile) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !paused) {
            paused = true;
            cancelAnimationFrame(animId);
          } else if (!entry.isIntersecting && paused) {
            paused = false;
            startLoop();
          }
        },
        { threshold: 0.1 },
      );
    }

    resize();
    window.addEventListener("resize", resize, { passive: true });
    startLoop();
    if (contactSection && observer) observer.observe(contactSection);

    return () => {
      cancelAnimationFrame(animId);
      if (resizeFrameId) {
        cancelAnimationFrame(resizeFrameId);
      }
      window.removeEventListener("resize", resize);
      observer?.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ opacity: 0.18, zIndex: 0 }}
    />
  );
}
